import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Coins, Check, History } from 'lucide-react';
import { api } from '@/lib/api';
import { luxe, gradientCss } from '@/lib/luxe';
import { GlassCard, LuxeButton, SectionTitle, Input, Field, Spinner, Empty, StatCard } from '@/components/bits';

export default function Wallet() {
  const [balance, setBalance] = useState(0);
  const [purchased, setPurchased] = useState(0);
  const [packages, setPackages] = useState([]);
  const [wallets, setWallets] = useState({});
  const [enabled, setEnabled] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyPkg, setBuyPkg] = useState(null);
  const [buyForm, setBuyForm] = useState({ currency: 'USDT', tx_hash: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [b, p, h] = await Promise.all([
        api('/studio-credits/balance'),
        api('/studio-credits/packages'),
        api('/studio-credits/history').catch(() => ({ items: [] })),
      ]);
      setBalance(b.balance); setPurchased(b.total_purchased);
      setPackages(p.packages); setWallets(p.wallets); setEnabled(!!p.enabled);
      setHistory(h.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitBuy = async () => {
    if (!buyForm.tx_hash.trim() || buyForm.tx_hash.trim().length < 10) return toast.error('Enter a valid transaction hash');
    try {
      await api('/studio-credits/buy', { method: 'POST', body: { package_id: buyPkg.id, currency: buyForm.currency, tx_hash: buyForm.tx_hash.trim() } });
      toast.success('Payment submitted — admin will verify within 24h');
      setBuyPkg(null); setBuyForm({ currency: 'USDT', tx_hash: '' }); load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner size={28} /></div>;

  return (
    <div>
      <SectionTitle eyebrow="Billing" title="Credits & Wallet" color={luxe.gold}
        desc="Top up credits to power AI across Software Coding & AI Marketing. Pay with crypto — approved by admin within 24 hours." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Available Credits" value={balance} accent={luxe.gold} />
        <StatCard label="Total Purchased" value={purchased} accent={luxe.blue} />
        <StatCard label="Transactions" value={history.length} accent={luxe.text} />
        <GlassCard className="p-5 flex items-center justify-center" style={{ background: gradientCss(['#E9C46A', '#D4AF37']) }}>
          <div className="text-center"><Coins size={26} color="#3A2C05" /><div className="mt-1 font-black text-sm" style={{ color: '#3A2C05' }}>MyRaai Wallet</div></div>
        </GlassCard>
      </div>

      {!enabled ? (
        <GlassCard className="p-8 mb-10 text-center" data-testid="credits-disabled-notice">
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(233,196,106,0.14)', border: '1px solid rgba(233,196,106,0.3)' }}><Coins size={26} style={{ color: luxe.gold }} /></div>
          <div className="font-display text-xl font-black" style={{ color: luxe.text }}>Credit purchases are currently closed</div>
          <div className="text-sm mt-1 max-w-md mx-auto" style={{ color: luxe.muted }}>Abhi credit buy option band hai. Jab available hoga, packages yahan dikhenge. Zaroorat ho toh admin se sampark karein.</div>
        </GlassCard>
      ) : (
        <>
          <div className="text-sm font-bold mb-3" style={{ color: luxe.text }}>Credit Packages</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {packages.map((pk) => (
              <GlassCard key={pk.id} className="p-5 hover-lift relative" style={pk.popular ? { border: `1.5px solid ${luxe.gold}` } : {}} data-testid={`package-${pk.id}`}>
                {pk.popular && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-black" style={{ background: gradientCss(['#E9C46A', '#D4AF37']), color: '#3A2C05' }}>POPULAR</div>}
                <div className="text-3xl mb-1">{pk.emoji}</div>
                <div className="font-display text-xl font-black" style={{ color: luxe.text }}>{pk.name}</div>
                <div className="mt-2 flex items-end gap-1">
                  <span className="text-3xl font-black gold-text">{pk.credits}</span>
                  <span className="text-xs mb-1" style={{ color: luxe.muted }}>credits</span>
                </div>
                <div className="text-sm font-bold mt-1" style={{ color: luxe.blue }}>${pk.usd_price}</div>
                <ul className="mt-3 space-y-1.5">
                  {pk.highlights.slice(0, 4).map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ color: luxe.textMid }}><Check size={13} style={{ color: luxe.success, marginTop: 1 }} />{h}</li>
                  ))}
                </ul>
                <LuxeButton full className="mt-4" onClick={() => setBuyPkg(pk)}>Buy Now</LuxeButton>
              </GlassCard>
            ))}
          </div>
        </>
      )}

      <div className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: luxe.text }}><History size={16} /> Transaction History</div>
      {history.length === 0 ? <Empty icon={History} title="No transactions yet" /> : (
        <div className="space-y-2">
          {history.map((t) => (
            <GlassCard key={t.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm capitalize" style={{ color: luxe.text }}>{(t.type || '').replace('_', ' ')}</div>
                <div className="text-xs" style={{ color: luxe.muted }}>{(t.created_at || '').slice(0, 16).replace('T', ' ')} · {t.status}</div>
              </div>
              <div className="font-black" style={{ color: t.amount >= 0 ? luxe.success : luxe.danger }}>{t.amount >= 0 ? '+' : ''}{t.amount}</div>
            </GlassCard>
          ))}
        </div>
      )}

      {buyPkg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(11,21,51,0.5)' }} onClick={() => setBuyPkg(null)}>
          <GlassCard className="w-full max-w-md p-6 float-in" style={{ background: '#12142B' }} onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl font-black mb-1" style={{ color: luxe.text }}>Buy {buyPkg.name}</div>
            <div className="text-sm mb-4" style={{ color: luxe.muted }}>{buyPkg.credits} credits · ${buyPkg.usd_price}</div>
            <Field label="Pay with">
              <select value={buyForm.currency} onChange={(e) => setBuyForm({ ...buyForm, currency: e.target.value })} className="w-full outline-none" style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${luxe.border}`, background: '#0B0D1E', color: luxe.text }}>
                {Object.keys(buyPkg.crypto_prices || {}).map((c) => <option key={c} value={c}>{c} — {buyPkg.crypto_prices[c]}</option>)}
              </select>
            </Field>
            <div className="p-3 rounded-xl mb-3 text-xs break-all" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, color: luxe.textMid }}>
              <div className="font-bold mb-1" style={{ color: luxe.text }}>Send to our {buyForm.currency} wallet:</div>
              <span className="font-mono">{wallets[buyForm.currency] || '—'}</span>
            </div>
            <Field label="Your Transaction Hash"><Input value={buyForm.tx_hash} onChange={(e) => setBuyForm({ ...buyForm, tx_hash: e.target.value })} placeholder="0x…" data-testid="buy-txhash" /></Field>
            <div className="flex gap-2 mt-2">
              <LuxeButton full onClick={submitBuy} data-testid="buy-submit">Submit Payment</LuxeButton>
              <LuxeButton variant="ghost" onClick={() => setBuyPkg(null)}>Cancel</LuxeButton>
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
}
